// Thin request/response helpers for the Chat Groups "Broadcast" modal.
// These hit the goserver /v2/cms_user/chat_broadcast/* endpoints (SUPER_USER /
// CARE only — the goserver enforces the role check too) and unwrap the standard
// house envelope (response.data.data), mirroring the thin-helper pattern in
// views/twilio_agent/settings_api.js. Every call rejects with the axios error so
// callers can surface the server's `reason` via broadcastErrorReason.

import { get_api as getApi, post_api as postApi } from '../../../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../../../utils/urls';

const BASE = '/v2/cms_user/chat_broadcast';

export interface PublicGroup {
  group_uuid: string;
  name: string;
  avatar: string;
  member_count: number;
}

// Media descriptor mirroring how the chat send path fills a Media object from
// the S3 upload result (see myt_chat/chat/network/MessageHandler.ts:1101-1116):
// fdk = the s3 key (uploadResult.fileKey, NOT the signed url), mt = mime type,
// fz = byte size. No `ftk` (thumb key): for images the chat client sets
// thumbKey to the *same* fileKey as dataKey — a separate thumb is only ever
// generated/uploaded for videos — so the server can derive the thumb key from
// fdk and we don't send a redundant field.
export interface BroadcastMedia {
  fdk: string;
  mt: string;
  fz: number;
}

export interface BroadcastPayload {
  text: string;
  media?: BroadcastMedia | null;
  target_mode: 'all' | 'selected';
  group_uuids: string[];
}

// Counter set shared by the POST reply and the GET poll. Everything the
// background fan-out fills in is optional: goserver#964 introduces `accepted`,
// `delivered` and `reconciled_at`, and until it is deployed the POST still
// answers with `published` alone — callers fall back to it.
export interface BroadcastSummary {
  broadcast_uuid: string;
  total_groups: number;
  published: number;
  accepted?: number;
  delivered?: number;
  failed: number;
  // null until the server has counted what actually landed; `delivered` is
  // meaningless before that, and is NOT to be read as 0.
  reconciled_at?: string | null;
  queued?: boolean;
  target_mode?: 'all' | 'selected';
  created_at?: string;
  // Only the POST reply carries the skip list; the stored row does not.
  skipped_group_uuids?: string[];
}

// Pull the human-readable failure reason out of an axios error (house envelope
// {"status":"failure","reason":...}), falling back to a caller-supplied default.
export const broadcastErrorReason = (error: any, fallback: string): string => {
  const data = error && error.response && error.response.data;
  return (data && (data.reason || data.message)) || fallback;
};

// GET /public_groups → [{ group_uuid, name, avatar, member_count }].
export const fetchPublicGroups = (): Promise<PublicGroup[]> =>
  getApi(getApiUrl(`${BASE}/public_groups`, false, true), true).then(
    (r: any) => (r.data && r.data.data && r.data.data.groups) || []
  );

// POST / { text, media, target_mode, group_uuids } → broadcast summary.
export const sendBroadcast = (payload: BroadcastPayload): Promise<BroadcastSummary> =>
  postApi(getApiUrl(BASE, false, true), payload, true).then(
    (r: any) => (r.data && r.data.data) || {}
  );

// GET /:uuid → the live broadcast row (counters + reconciled_at). Rejects with
// the axios error, so a 404 (endpoint not deployed yet, or unknown uuid) is
// distinguishable via error.response.status.
export const fetchBroadcast = (uuid: string): Promise<BroadcastSummary> =>
  getApi(getApiUrl(`${BASE}/${uuid}`, false, true), true).then(
    (r: any) => (r.data && r.data.data) || {}
  );
